import {ResponseError} from "./ResponseError";

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
 */
export class ErrorPresets {
	static get e400() {
		return new ResponseError({
			statusCode: 400,
			error: "BAD_REQUEST",
			message: `The server cannot or will not process the request due to something that is perceived to be a client error (e.g., malformed request syntax, invalid request message framing, or deceptive request routing).`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e401() {
		return new ResponseError({
			statusCode: 401,
			error: "UNAUTHORIZED",
			message: `Although the HTTP standard specifies "unauthorized", semantically this response means "unauthenticated". That is, the client must authenticate itself to get the requested response.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e402() {
		return new ResponseError({
			statusCode: 402,
			error: "PAYMENT_REQUIRED_EXPERIMENTAL",
			message: `This response code is reserved for future use. The initial aim for creating this code was using it for digital payment systems, however this status code is used very rarely and no standard convention exists.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e403() {
		return new ResponseError({
			statusCode: 403,
			error: "FORBIDDEN",
			message: `The client does not have access rights to the content; that is, it is unauthorized, so the server is refusing to give the requested resource. Unlike 401 Unauthorized, the client's identity is known to the server.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e404() {
		return new ResponseError({
			statusCode: 404,
			error: "NOT_FOUND",
			message: `The server cannot find the requested resource. In the browser, this means the URL is not recognized. In an API, this can also mean that the endpoint is valid but the resource itself does not exist. Servers may also send this response instead of 403 Forbidden to hide the existence of a resource from an unauthorized client. This response code is probably the most well known due to its frequent occurrence on the web.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e405() {
		return new ResponseError({
			statusCode: 405,
			error: "METHOD_NOT_ALLOWED",
			message: `The request method is known by the server but is not supported by the target resource. For example, an API may not allow calling DELETE to remove a resource.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e406() {
		return new ResponseError({
			statusCode: 406,
			error: "NOT_ACCEPTABLE",
			message: `This response is sent when the web server, after performing server-driven content negotiation, doesn't find any content that conforms to the criteria given by the user agent.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e407() {
		return new ResponseError({
			statusCode: 407,
			error: "PROXY_AUTHENTICATION_REQUIRED",
			message: `This is similar to 401 Unauthorized but authentication is needed to be done by a proxy.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e408() {
		return new ResponseError({
			statusCode: 408,
			error: "REQUEST_TIMEOUT",
			message: `This response is sent on an idle connection by some servers, even without any previous request by the client. It means that the server would like to shut down this unused connection. This response is used much more since some browsers, like Chrome, Firefox 27+, or IE9, use HTTP pre-connection mechanisms to speed up surfing. Also note that some servers merely shut down the connection without sending this message.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e409() {
		return new ResponseError({
			statusCode: 409,
			error: "CONFLICT",
			message: `This response is sent when a request conflicts with the current state of the server.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e410() {
		return new ResponseError({
			statusCode: 410,
			error: "GONE",
			message: `This response is sent when the requested content has been permanently deleted from server, with no forwarding address. Clients are expected to remove their caches and links to the resource. The HTTP specification intends this status code to be used for "limited-time, promotional services". APIs should not feel compelled to indicate resources that have been deleted with this status code.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e411() {
		return new ResponseError({
			statusCode: 411,
			error: "LENGTH_REQUIRED",
			message: `Server rejected the request because the Content-Length header field is not defined and the server requires it.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e412() {
		return new ResponseError({
			statusCode: 412,
			error: "PRECONDITION_FAILED",
			message: `The client has indicated preconditions in its headers which the server does not meet.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e413() {
		return new ResponseError({
			statusCode: 413,
			error: "PAYLOAD_TOO_LARGE",
			message: `Request entity is larger than limits defined by server. The server might close the connection or return an Retry-After header field.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e414() {
		return new ResponseError({
			statusCode: 414,
			error: "URI_TOO_LONG",
			message: `The URI requested by the client is longer than the server is willing to interpret.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e415() {
		return new ResponseError({
			statusCode: 415,
			error: "UNSUPPORTED_MEDIA_TYPE",
			message: `The media format of the requested data is not supported by the server, so the server is rejecting the request.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e416() {
		return new ResponseError({
			statusCode: 416,
			error: "RANGE_NOT_SATISFIABLE",
			message: `The range specified by the Range header field in the request cannot be fulfilled. It's possible that the range is outside the size of the target URI's data.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e417() {
		return new ResponseError({
			statusCode: 417,
			error: "EXPECTATION_FAILED",
			message: `This response code means the expectation indicated by the Expect request header field cannot be met by the server.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e421() {
		return new ResponseError({
			statusCode: 421,
			error: "MISDIRECTED_REQUEST",
			message: `The request was directed at a server that is not able to produce a response. This can be sent by a server that is not configured to produce responses for the combination of scheme and authority that are included in the request URI.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e422() {
		return new ResponseError({
			statusCode: 422,
			error: "UNPROCESSABLE_ENTITY (WEB_DAV)",
			message: `The request was well-formed but was unable to be followed due to semantic errors.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e423() {
		return new ResponseError({
			statusCode: 423,
			error: "LOCKED (WEB_DAV)",
			message: `The resource that is being accessed is locked.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e424() {
		return new ResponseError({
			statusCode: 424,
			error: "FAILED_DEPENDENCY (WEB_DAV)",
			message: `The request failed due to failure of a previous request.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e425() {
		return new ResponseError({
			statusCode: 425,
			error: "TOO_EARLY_EXPERIMENTAL",
			message: `Indicates that the server is unwilling to risk processing a request that might be replayed.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e426() {
		return new ResponseError({
			statusCode: 426,
			error: "UPGRADE_REQUIRED",
			message: `The server refuses to perform the request using the current protocol but might be willing to do so after the client upgrades to a different protocol. The server sends an Upgrade header in a 426 response to indicate the required protocol(s).`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e428() {
		return new ResponseError({
			statusCode: 428,
			error: "PRECONDITION_REQUIRED",
			message: `The origin server requires the request to be conditional. This response is intended to prevent the 'lost update' problem, where a client GETs a resource's state, modifies it and PUTs it back to the server, when meanwhile a third party has modified the state on the server, leading to a conflict.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e429() {
		return new ResponseError({
			statusCode: 429,
			error: "TOO_MANY_REQUESTS",
			message: `The user has sent too many requests in a given amount of time ("rate limiting").`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e431() {
		return new ResponseError({
			statusCode: 431,
			error: "REQUEST_HEADER_FIELDS_TOO_LARGE",
			message: `The server is unwilling to process the request because its header fields are too large. The request may be resubmitted after reducing the size of the request header fields.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e451() {
		return new ResponseError({
			statusCode: 451,
			error: "UNAVAILABLE_FOR_LEGAL_REASONS",
			message: `The user agent requested a resource that cannot legally be provided, such as a web page censored by a government.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e500() {
		return new ResponseError({
			statusCode: 500,
			error: "INTERNAL_SERVER_ERROR",
			message: `The server has encountered a situation it does not know how to handle.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e501() {
		return new ResponseError({
			statusCode: 501,
			error: "NOT_IMPLEMENTED",
			message: `The request method is not supported by the server and cannot be handled. The only methods that servers are required to support (and therefore that must not return this code) are GET and HEAD.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e502() {
		return new ResponseError({
			statusCode: 502,
			error: "BAD_GATEWAY",
			message: `This error response means that the server, while working as a gateway to get a response needed to handle the request, got an invalid response.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e503() {
		return new ResponseError({
			statusCode: 503,
			error: "SERVICE_UNAVAILABLE",
			message: `The server is not ready to handle the request.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e504() {
		return new ResponseError({
			statusCode: 504,
			error: "GATEWAY_TIMEOUT",
			message: `This error response is given when the server is acting as a gateway and cannot get a response in time.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e505() {
		return new ResponseError({
			statusCode: 505,
			error: "HTTP_VERSION_NOT_SUPPORTED",
			message: `The HTTP version used in the request is not supported by the server.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e506() {
		return new ResponseError({
			statusCode: 506,
			error: "VARIANT_ALSO_NEGOTIATES",
			message: `The server has an internal configuration error: the chosen variant resource is configured to engage in transparent content negotiation itself, and is therefore not a proper end point in the negotiation process.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e507() {
		return new ResponseError({
			statusCode: 507,
			error: "INSUFFICIENT_STORAGE (WEB_DAV)",
			message: `The method could not be performed on the resource because the server is unable to store the representation needed to successfully complete the request.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e508() {
		return new ResponseError({
			statusCode: 508,
			error: "LOOP_DETECTED (WEB_DAV)",
			message: `The server detected an infinite loop while processing the request.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e510() {
		return new ResponseError({
			statusCode: 510,
			error: "NOT_EXTENDED",
			message: `Further extensions to the request are required for the server to fulfill it.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}

	static get e511() {
		return new ResponseError({
			statusCode: 511,
			error: "NETWORK_AUTHENTICATION_REQUIRED",
			message: `Indicates that the client needs to authenticate to gain network access.`,
			time: new Date().toISOString(),
			timestamp: Date.now(),
		});
	}
}
